import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import redemptionService, { RedemptionStatus } from '../../services/redemptionService';
import RedemptionConnectPanel from '@shared/RedemptionConnectPanel';
import { useSearchParams } from 'react-router-dom';

const REQUIREMENT_LABELS: Record<string, string> = {
  ENROLLMENT_REQUIRED: 'Start redemption enrollment',
  US_RESIDENCY_REQUIRED: 'United States resident or business',
  EARNINGS_THRESHOLD: 'Earn at least $100 in platform-paid Qix Credits',
  ID_VERIFICATION_REQUIRED: 'Verify government ID',
  PHONE_VERIFICATION_REQUIRED: 'Verify phone number',
  ADDRESS_VERIFICATION_REQUIRED: 'Verify mailing address',
  NAME_BINDING_REQUIRED: 'Bind true name to a verified alias',
};

interface RedemptionEligibilityPanelProps {
  /** True only when this login is a BUSINESS joint-account session. */
  businessJointSession?: boolean;
}

const BUSINESS_REQUIREMENT_LABELS: Record<string, string> = {
  NAME_BINDING_REQUIRED: 'Verify identity matches your authorized team profile name',
};

function requirementMet(key: string, status: RedemptionStatus): boolean {
  return !status.missingRequirements.includes(key);
}

const RedemptionEligibilityPanel: React.FC<RedemptionEligibilityPanelProps> = ({
  businessJointSession = false,
}) => {
  const [searchParams] = useSearchParams();
  const connectReturnHint = searchParams.get('connect');
  const [status, setStatus] = useState<RedemptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await redemptionService.getStatus();
      setStatus(s);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: string } };
      if (err.response?.status === 500) {
        setError('Redemption program is not available yet (database migration may be pending).');
      } else {
        setError('Could not load redemption status.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleEnroll = async () => {
    setEnrolling(true);
    setError(null);
    setMessage(null);
    try {
      const result = await redemptionService.enroll(true);
      setStatus(result.status);
      setMessage(result.message);
    } catch {
      setError('Enrollment failed. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const checklistKeys = [
    'ENROLLMENT_REQUIRED',
    'US_RESIDENCY_REQUIRED',
    'EARNINGS_THRESHOLD',
    'ID_VERIFICATION_REQUIRED',
    'PHONE_VERIFICATION_REQUIRED',
    'ADDRESS_VERIFICATION_REQUIRED',
    'NAME_BINDING_REQUIRED',
  ];

  const simulateConnect = useMemo(
    () => (import.meta.env.DEV
      ? (outcome: 'approved' | 'rejected' | 'reset') => redemptionService.simulateConnect(outcome)
      : undefined),
    [],
  );

  return (
    <Card elevation={3} sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SavingsIcon color="primary" />
              Cash Redemption Program
            </Typography>
            {loading ? (
              <CircularProgress size={20} />
            ) : status?.isAuthorizedForRedemption ? (
              <Chip color="success" label="Authorized — use Redeem Credits in Account History" />
            ) : status ? (
              <Chip label={status.enrollmentStatus.replace(/_/g, ' ')} variant="outlined" />
            ) : null}
          </Box>

          <Typography variant="body2" color="text.secondary">
            Qix Credits are platform units, not cash. Eligible users may convert earned credits to U.S. dollars via ACH
            after verification. See{' '}
            <Link href="https://qixit.com/redemption-program/" target="_blank" rel="noopener noreferrer">
              Redemption Program Terms
            </Link>
            .
          </Typography>

          {message && <Alert severity="success">{message}</Alert>}
          {error && <Alert severity={status ? 'error' : 'warning'}>{error}</Alert>}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && status?.enrollmentStatus === 'NOT_STARTED' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Start enrollment to complete identity and tax onboarding required for cash redemption.
              </Typography>
              <Button variant="contained" onClick={handleEnroll} disabled={enrolling}>
                {enrolling ? 'Starting…' : 'Start redemption enrollment'}
              </Button>
            </Box>
          )}

          {!loading && status && status.enrollmentStatus !== 'NOT_STARTED' && (
            <>
              <Divider />
              <Typography variant="subtitle2">Eligibility checklist</Typography>
              <List dense disablePadding>
                {checklistKeys.map((key) => {
                  const met = requirementMet(key, status);
                  if (key === 'ENROLLMENT_REQUIRED' && status.enrollmentStatus !== 'NOT_STARTED') {
                    return (
                      <ListItem key={key} disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Enrollment started" />
                      </ListItem>
                    );
                  }
                  if (key === 'ENROLLMENT_REQUIRED') return null;
                  const businessMode = businessJointSession;
                  const label = (businessMode && BUSINESS_REQUIREMENT_LABELS[key])
                    ? BUSINESS_REQUIREMENT_LABELS[key]!
                    : (REQUIREMENT_LABELS[key] || key);
                  return (
                    <ListItem key={key} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {met ? <CheckIcon color="success" fontSize="small" /> : <PendingIcon color="disabled" fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        secondary={
                          key === 'EARNINGS_THRESHOLD'
                            ? `${status.lifetimeEarned.toFixed(2)} / ${status.minQualificationEarned.toFixed(2)} Qix Credits earned`
                            : key === 'NAME_BINDING_REQUIRED' && businessMode && !met
                              ? (
                                <>
                                  Verify government ID in Business Information so your ID matches your Team Member
                                  Profile display name.{' '}
                                  <Link href="/profile?tab=account-settings#business-redemption-identity">
                                    Open business redemption identity
                                  </Link>
                                </>
                              )
                              : undefined
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>

              {!status.isAuthorizedForRedemption && status.enrollmentStatus === 'PENDING_VERIFICATION' && (
                <Button variant="outlined" size="small" onClick={load}>
                  Refresh status
                </Button>
              )}

              <Divider />
              <Typography variant="subtitle2">Legal acceptances</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enrollment includes acceptance of redemption program terms, earnings disclaimer, and tax reporting consent.
              </Typography>
              <List dense disablePadding>
                {['REDEMPTION_TERMS', 'EARNINGS_DISCLAIMER', 'TAX_CONSENT'].map((docType) => {
                  const enrolled = status.enrollmentStatus !== 'NOT_STARTED';
                  const met = enrolled;
                  return (
                    <ListItem key={docType} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {met ? <CheckIcon color="success" fontSize="small" /> : <PendingIcon color="disabled" fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={docType.replace(/_/g, ' ')}
                        secondary={met ? 'Accepted at enrollment' : 'Pending enrollment'}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {!loading && status?.isAuthorizedForRedemption && (
            <RedemptionConnectPanel
              getConnectStatus={() => redemptionService.getConnectStatus()}
              startConnectOnboarding={(returnUrl, refreshUrl) =>
                redemptionService.startConnectOnboarding(returnUrl, refreshUrl)}
              simulateConnect={simulateConnect}
              connectReturnHint={connectReturnHint}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RedemptionEligibilityPanel;
